import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner } from './shared';

const TRIGGER_COLORS = {
    before_model: 'blue',
    after_model: 'purple',
    before_tool: 'amber',
    after_tool: 'green',
    before_agent: 'gray',
};

export default function AdminReminders() {
    const navigate = useNavigate();
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [filterTrigger, setFilterTrigger] = useState('');

    useEffect(() => { loadReminders(); }, []);

    const loadReminders = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterGroup) params.group = filterGroup;
            if (filterTrigger) params.trigger = filterTrigger;
            const res = await adminApi.listReminders(params);
            setReminders(res.reminders || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load reminders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadReminders(); }, [filterGroup, filterTrigger]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!confirm(`Delete reminder "${id}"?`)) return;
        try {
            await adminApi.deleteReminder(id, true);
            setSuccessMessage(`Reminder "${id}" deleted`);
            loadReminders();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const groups = [...new Set(reminders.map(r => r.group).filter(Boolean))];
    const triggers = [...new Set(reminders.map(r => r.trigger))];

    const filtered = reminders.filter(r => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return r.id.toLowerCase().includes(q) ||
               r.name.toLowerCase().includes(q) ||
               (r.group || '').toLowerCase().includes(q);
    });

    if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">System Reminders</h2>
                <Button onClick={() => navigate('/admin/reminders/new')}>Add Reminder</Button>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert type="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            <div className="flex gap-3 items-center flex-wrap">
                <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search reminders..." />
                <select
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    value={filterGroup}
                    onChange={e => setFilterGroup(e.target.value)}
                >
                    <option value="">All groups</option>
                    {groups.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <select
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    value={filterTrigger}
                    onChange={e => setFilterTrigger(e.target.value)}
                >
                    <option value="">All triggers</option>
                    {triggers.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">ID / Name</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Trigger</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Inject As</th>
                            <th className="px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Priority</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Group</th>
                            <th className="px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Conditions</th>
                            <th className="px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Status</th>
                            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {filtered.map(r => (
                            <tr
                                key={r.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                                onClick={() => navigate(`/admin/reminders/${r.id}`)}
                            >
                                <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900 dark:text-white">{r.id}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{r.name}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <Badge color={TRIGGER_COLORS[r.trigger] || 'gray'}>{r.trigger}</Badge>
                                </td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.inject_as}</td>
                                <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{r.priority}</td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.group || '-'}</td>
                                <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{r.conditions?.length || 0}</td>
                                <td className="px-4 py-3 text-center">
                                    <Badge color={r.is_active ? 'green' : 'gray'}>{r.is_active ? 'active' : 'inactive'}</Badge>
                                </td>
                                <td className="px-4 py-3 text-right space-x-2">
                                    <button
                                        onClick={async e => {
                                            e.stopPropagation();
                                            try {
                                                await adminApi.toggleReminder(r.id);
                                                loadReminders();
                                            } catch (err) { setError(err.message); }
                                        }}
                                        className={`text-xs ${r.is_active ? 'text-amber-500 hover:text-amber-700' : 'text-green-500 hover:text-green-700'}`}
                                    >
                                        {r.is_active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                        onClick={e => handleDelete(e, r.id)}
                                        className="text-red-500 hover:text-red-700 text-xs"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    No reminders found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
